"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

export type Theme = "light" | "dark" | "system";

const VALID: readonly Theme[] = ["light", "dark", "system"] as const;
const DEFAULT_THEME: Theme = "system";
const STORAGE_KEY = "theme";

interface ThemeCtx {
  theme: Theme;
  resolved: "light" | "dark";
  setTheme: (t: Theme) => void;
  mounted: boolean;
}

const Ctx = createContext<ThemeCtx | null>(null);

function isValidTheme(v: unknown): v is Theme {
  return typeof v === "string" && (VALID as readonly string[]).includes(v);
}

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_THEME;
    const normalized = raw.trim().toLowerCase();
    if (isValidTheme(normalized)) {
      if (normalized !== raw) {
        try {
          window.localStorage.setItem(STORAGE_KEY, normalized);
        } catch {
          /* ignore */
        }
      }
      return normalized;
    }
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

function safeWriteTheme(t: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, t);
  } catch {
    /* ignore */
  }
}

function applyTheme(theme: Theme): "light" | "dark" {
  const sysDark =
    typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
  const resolved = theme === "system" ? (sysDark ? "dark" : "light") : theme;
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
  return resolved;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(DEFAULT_THEME);
  const [resolved, setResolved] = useState<"light" | "dark">("dark");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const stored = readStoredTheme();
    setThemeState(stored);
    setResolved(applyTheme(stored));
    setMounted(true);

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemChange = () => {
      const cur = readStoredTheme();
      if (cur === "system") setResolved(applyTheme("system"));
    };
    mq.addEventListener("change", onSystemChange);

    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY && e.key !== null) return;
      const next = readStoredTheme();
      setThemeState(next);
      setResolved(applyTheme(next));
    };
    window.addEventListener("storage", onStorage);

    return () => {
      mq.removeEventListener("change", onSystemChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setTheme = (t: Theme) => {
    const safe: Theme = isValidTheme(t) ? t : DEFAULT_THEME;
    safeWriteTheme(safe);
    setThemeState(safe);
    setResolved(applyTheme(safe));
  };

  return <Ctx.Provider value={{ theme, resolved, setTheme, mounted }}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useTheme must be used within ThemeProvider");
  return c;
}
